import moment from "moment";

import { getVersionAuthor, type VersionSelectorVersion } from "./types";

import LabelBadge from "@/components/ui/LabelBadge";
import { formatTimestamp } from "@/utils/timestamp";

interface VersionRowTooltipContentProps {
  version: VersionSelectorVersion;
  dynamicReleaseLabels?: { id: number; name: string }[];
}

export const VersionRowTooltipContent = ({
  version,
  dynamicReleaseLabels = [],
}: VersionRowTooltipContentProps) => {
  const author = getVersionAuthor(version);
  const createdMoment = moment(version.created_at);
  const relativeTimestamp = createdMoment.isValid()
    ? formatTimestamp(createdMoment)
    : "Invalid date";
  const absoluteTimestamp = createdMoment.isValid()
    ? createdMoment.format("MMM D, YYYY [at] h:mm A")
    : "Invalid date";

  return (
    <div className="space-y-2">
      <div className="font-medium text-gray-900">
        Version {version.number}
        {version.archived ? " (archived)" : ""}
      </div>
      <div className="space-y-1 text-xs text-gray-600">
        <div>
          <span className="font-medium text-gray-800">Created by:</span> {author || "Unknown"}
        </div>
        <div>
          <span className="font-medium text-gray-800">When:</span> {relativeTimestamp}
        </div>
        <div className="text-[11px] text-gray-500">{absoluteTimestamp}</div>
      </div>
      <div className="space-y-1">
        <div className="text-xs font-medium text-gray-800">Commit message</div>
        <div className="text-xs leading-relaxed text-gray-600">
          {version.commit_message || "No commit message"}
        </div>
      </div>
      {(version.labels.length > 0 || dynamicReleaseLabels.length > 0) && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-gray-800">Labels</div>
          <div className="flex flex-wrap gap-1">
            {version.labels.map(label => (
              <LabelBadge
                key={`release-${label.id}`}
                label={label.name}
                variant="environment"
                showTooltip={false}
              />
            ))}
            {dynamicReleaseLabels.map(label => (
              <LabelBadge
                key={`dynamic-${label.id}`}
                label={label.name}
                variant="environment"
                className="border-dashed"
                showTooltip={false}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
